import { PERMISSIONS, ROLE_KEYS, type PermissionKey, type SessionUser } from '@ashniva/types';

import { useSession } from '../auth/session-context';

/** What the signed-in person may do with Repository Intelligence, as far as the screens can tell. */
export interface RepositoryIntelligenceAccess {
  /** Status, search, files and symbols. */
  query: boolean;
  /** Questions about code. */
  ask: boolean;
  /** Change analyses and the proposals made from them. */
  analyze: boolean;
  /** Registering, syncing and removing repositories, and the organization's settings. */
  manage: boolean;
}

/**
 * The API's rule, mirrored so that nobody is shown a screen that can only refuse them: the permission, held by the
 * **system** Super Admin of the service-provider organization. A custom role cloned from Super Admin reports the
 * template's key, so `isCustomRole` is what tells it apart.
 *
 * Presentation only. The API re-reads the stored role on every request and refuses everyone else whatever this says;
 * when the rule widens (the owner's decision Q-15), this is the one place the screens change.
 */
export function repositoryIntelligenceAccess(
  user: SessionUser | null,
): RepositoryIntelligenceAccess {
  const eligible =
    user !== null &&
    user.roleKey === ROLE_KEYS.SUPER_ADMIN &&
    !user.isCustomRole &&
    user.organization.isServiceProvider;
  const has = (permission: PermissionKey) => eligible && user.permissions.includes(permission);
  return {
    query: has(PERMISSIONS.REPOSITORY_INTELLIGENCE_QUERY),
    ask: has(PERMISSIONS.REPOSITORY_INTELLIGENCE_ASK),
    analyze: has(PERMISSIONS.REPOSITORY_INTELLIGENCE_ANALYZE),
    manage: has(PERMISSIONS.REPOSITORY_INTELLIGENCE_MANAGE),
  };
}

export function useRepositoryIntelligenceAccess(): RepositoryIntelligenceAccess {
  const { user } = useSession();
  return repositoryIntelligenceAccess(user);
}
